<template>
  <b-container class="edit-society mt-4">
    <v-wait :for="loaderName">
      <template slot="waiting">
        <HalfCircleSpinner />
        <div class="spinner-message my-3 text-center">
          Loading society data...
        </div>
      </template>
      <society-form
        v-if="society"
        :initial-society="society"
        :save-loading="saveLoading"
        @form-submit="save"
        @cancel="cancel"
      >
        <template v-slot:internal-content>
          <b-row class="m-3">
            <b-col sm="12">
              <b-form-group
                label-cols-horizontal
                label-cols="3"
                label="Members:"
                label-class="font-weight-bold"
              >
                <b-button variant="primary" @click.prevent="onViewMembers">
                  View Members List
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="m-3" align-h="end" v-if="statistics">
            <b-col cols="9">
              <b-row>
                <b-col>Active Members: {{ statistics.users.active }}</b-col>
                <b-col>Disabled Members: {{ statistics.users.disabled }}</b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row class="m-3">
            <b-col sm="12">
              <b-form-group
                label-cols-horizontal
                label-cols="3"
                label="Send invitation emails:"
                label-class="font-weight-bold"
              >
                <div class="pt-2" v-if="statistics">
                  <p>
                    Members To Receive Emails:
                    {{ statistics.members.notSentRegEmail }}
                  </p>
                  <p>
                    Members Sent Email But Not Registered:
                    {{ statistics.members.notRegistered }}
                  </p>
                </div>
                <b-button variant="primary" @click.prevent="onSendRegEmails">
                  <template v-if="!sendRegEmailsLoading">Send</template>
                  <template v-else>
                    <b-spinner small />
                    <span>Loading...</span>
                  </template>
                </b-button>
                <b-form-checkbox
                  class="ml-3 mt-3"
                  v-model="allowResending"
                  inline
                >
                  Allow resending of e-mails
                </b-form-checkbox>
                <b-form-checkbox
                  class="ml-3 mt-3"
                  v-model="doNotOverwriteTokens"
                  inline
                >
                  Do not overwrite existing tokens
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="m-3">
            <b-col class="pt-3">
              <b-form-group label-cols-horizontal label-cols="3" label="">
                <b-button
                  variant="primary"
                  target="_blank"
                  :href="downloadInvitationListLink"
                >
                  Download Invitation List
                </b-button>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group>
                <b-form-radio-group
                  id="type-of-members"
                  v-model="typeOfMembers"
                  :options="memberTypeOptions"
                  name="radio-options"
                />
              </b-form-group>
              <b-form-group>
                <b-form-checkbox v-model="doNotOverwriteExisted">
                  Not Overwrite Existing Token
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="m-3">
            <b-col sm="12">
              <b-form-group
                label-cols-horizontal
                label-cols="3"
                label="Send Emails:"
                label-class="font-weight-bold"
              >
                <b-button
                  variant="primary"
                  @click.prevent="showDistributionEmailBuildingModal"
                >
                  Build Emails
                </b-button>
                <b-button
                  variant="primary"
                  class="ml-3"
                  @click.prevent="showTestEmailModal"
                >
                  Test Email
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
        </template>
      </society-form>
      <b-container>
        <b-row>
          <b-col cols="12">
            <b-button
              v-if="society && society.id"
              variant="v2_primary mr-3"
              :to="`/admin/society/mapping/${society.id}`"
            >
              Society Member Type Mappings
            </b-button>
            <b-button
              v-if="society && society.id"
              variant="v2_primary mr-3"
              :to="`/admin/society/certifying-boards/${society.id}`"
            >
              Society Certifying Boards
            </b-button>
            <b-button
              variant="v2_primary"
              @click="isGenerateKpiModalOpen = true"
            >
              Generate KPIs
            </b-button>
          </b-col>
        </b-row>
      </b-container>
      <div ref="alertContainer" v-if="!!alertMsg">
        <b-alert
          :show="!!alertMsg"
          :variant="alertMsg.type"
          dismissible
          v-on:dismissed="closeAlert"
        >
          <h4 class="alert-heading">
            {{ alertMsg.type === 'danger' ? 'Error!' : 'Success!' }}
          </h4>
          <hr />
          <p class="font-weight-bold">{{ alertMsg.message }}</p>
        </b-alert>
      </div>
    </v-wait>
    <modal-re-init-modal-behaviour>
      <template slot-scope="{ keyId, keyUpdater }">
        <b-modal
          v-model="isGenerateKpiModalOpen"
          ok-only
          @show="keyUpdater"
          ok-title="Cancel"
          @ok="isGenerateKpiModalOpen = false"
          ok-variant="v2_white"
          size="lg"
        >
          <kpi-report-modal-content
            :society="society"
            :key="keyId"
            @cancel="isGenerateKpiModalOpen = false"
          />
        </b-modal>
      </template>
    </modal-re-init-modal-behaviour>
    <distribution-email-building-modal
      v-if="society"
      :visible="isDistributionEmailBuildingModalVisible"
      :society-id="parseInt(society.id)"
      @close="isDistributionEmailBuildingModalVisible = false"
    />
    <test-email-modal
      v-if="society"
      :visible="isTestEmailModalVisible"
      :society-id="parseInt(society.id)"
      @close="isTestEmailModalVisible = false"
    />
  </b-container>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Api, { apiUrl } from '@/providers/Api.js';
import SocietyForm from '@/components/society/SocietyForm';
import { HalfCircleSpinner } from 'epic-spinners';
import { UPDATE_SOCIETY_DATA_MUTATION } from '@/store';
import Help from '@/providers/Help';
import KpiReportModalContent from '@/components/society/kpi-report/KpiReportModalContent';
import ModalReInitModalBehaviour from '@/behaviours/ForceUpdateBehaviour';
import DistributionEmailBuildingModal from '@/components/society/DistributionEmailBuildingModal';
import TestEmailModal from '@/components/society/TestEmailModal';

const ommitExtraFields = ({
  memberEmails,
  secret_key,
  internalPerson,
  ...rest
}) => rest;

const MEMBER_TYPES = {
  UNREGISTERED: 'UNREGISTERED',
  EXPIRED: 'EXPIRED',
};

const MEMBER_TYPE_OPTIONS = [
  { value: MEMBER_TYPES.UNREGISTERED, text: 'All Unregistered' },
  { value: MEMBER_TYPES.EXPIRED, text: 'Only expired' },
];

export default {
  name: 'SocietyEdit',
  components: {
    SocietyForm,
    HalfCircleSpinner,
    KpiReportModalContent,
    ModalReInitModalBehaviour,
    DistributionEmailBuildingModal,
    TestEmailModal,
  },
  data: function () {
    return {
      society: null,
      add_specialty_id: null,
      sendRegEmailsLoading: false,
      sendRegEmailsError: null,
      alertMsg: null,
      allowResending: false,
      statistics: null,
      saveLoading: false,
      typeOfMembers: MEMBER_TYPES.UNREGISTERED,
      doNotOverwriteTokens: true,
      doNotOverwriteExisted: true,
      isGenerateKpiModalOpen: false,
      isDistributionEmailBuildingModalVisible: false,
      isTestEmailModalVisible: false,
    };
  },
  created: function () {
    this.getSociety();
    this.getStatistics();
  },
  computed: {
    loaderName: function () {
      return this.$options.name + '_loading';
    },
    downloadInvitationListLink: function () {
      return `${apiUrl}?r=download/invitation-list&society_id=${this.society.id}&invitation_type=${this.typeOfMembers}&do_not_overwrite_existed=${this.doNotOverwriteExisted}`;
    },
    memberTypeOptions: function () {
      return MEMBER_TYPE_OPTIONS;
    },
  },
  methods: {
    getSociety: async function () {
      this.$wait.start(this.loaderName);
      this.society = await Api.getSociety(this, this.$route.params.societyId);
      this.$wait.end(this.loaderName);
    },
    cancel: function () {
      this.$router.push('/admin/society');
    },
    save: async function (data) {
      this.saveLoading = true;
      const updatedSociety = {
        ...ommitExtraFields(this.society),
        ...data,
      };
      try {
        const responsedSociety = await Api.updateSociety(updatedSociety);
        this.society = null;
        this.$nextTick(() => {
          this.society = responsedSociety;
          this.$store.commit(UPDATE_SOCIETY_DATA_MUTATION, this.society);

          this.$toasted.success('Society saved', { duration: 3000 });
        });
      } catch (error) {
        process.env.NODE_ENV !== 'production' && console.error(error);
        this.$toasted.show('Some error occurs', {
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.saveLoading = false;
      }
    },
    onSendRegEmails: async function () {
      Help.confirm.show(
        'Are you sure that you want to send invitation links to members?',
        'Yes',
        () => {
          this.sendRegEmails();
        }
      );
    },
    sendRegEmails: async function () {
      this.sendRegEmailsLoading = true;
      try {
        const {
          success = false,
          emailsCount,
        } = await Api.sendInvitationToMembers(
          this.society.id,
          this.allowResending,
          this.doNotOverwriteTokens
        );
        if (success && emailsCount !== undefined) {
          this.sendRegEmailsLoading = false;
          this.alertMsg = {
            type: 'success',
            message: `Successfully sent ${emailsCount} emails`,
          };
          this.$nextTick(() => this.$refs.alertContainer.scrollIntoView());
        }
      } catch (error) {
        this.sendRegEmailsLoading = false;
        this.alertMsg = {
          type: 'danger',
          message:
            error && error.response && error.response.data
              ? error.response.data.message
              : 'Server error',
        };
        this.$nextTick(() => this.$refs.alertContainer.scrollIntoView());
      }
    },
    closeAlert: function () {
      this.alertMsg = null;
    },
    onViewMembers: function () {
      this.$router.push(
        `/admin/society/edit-members/${this.$route.params.societyId}`
      );
    },
    getStatistics: async function () {
      this.$wait.start(this.loaderName);
      try {
        this.statistics = await Api.getSocietyStatisticsOfMembers(
          this.$route.params.societyId
        );
      } catch (error) {
        const data = error.response.data;
        this.$toasted.error(data.message, { duration: 3000 });
      }
      this.$wait.end(this.loaderName);
    },
    syncSociety: function (updatedSociety) {
      for (let key in updatedSociety) {
        if (updatedSociety.hasOwnProperty(key)) {
          this.$set(this.society, key, updatedSociety[key]);
        }
      }
    },
    showDistributionEmailBuildingModal: function () {
      this.isDistributionEmailBuildingModalVisible = true;
    },
    showTestEmailModal: function () {
      this.isTestEmailModalVisible = true;
    },
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss" scoped>
.col-form-label {
  font-weight: bold;
}
</style>
